import cn from 'classnames';
import { useCallback } from 'react';

import s from './List.module.css';
import { Loader } from './Loader';

interface IColumnConfig<TEntity> {
  renderHeader: () => React.ReactNode;
  renderCell: (entity: TEntity, idx: number, rowIdx: number) => React.ReactNode;
  style?: Pick<React.CSSProperties, 'width'>;
}

interface IListProps<TEntity> {
  data: TEntity[];
  columns: IColumnConfig<TEntity>[];
  getEntityId: (entity: TEntity) => string;
  onClickRow?: (entity: TEntity | undefined) => void;
  isLoading?: boolean;
}

const List = <TEntity,>({
  data,
  columns,
  getEntityId,
  onClickRow,
  isLoading = false,
}: IListProps<TEntity>) => {
  const handleClickRow = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (!onClickRow) {
        return;
      }

      const id = event.currentTarget.dataset.entityId ?? '';
      const entity = data.find(item => getEntityId(item) === id);

      onClickRow(entity);
    },
    [data, getEntityId, onClickRow],
  );

  return (
    <>
      <table className={s.list}>
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th key={idx} style={col.style}>
                {col.renderHeader()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && data.length > 0 && <Loader />}
          {data.length > 0 &&
            data.map((row, rowIdx) => {
              const id = getEntityId(row);

              return (
                <tr key={id} data-entity-id={id} onClick={handleClickRow} className={s.row}>
                  {columns.map((col, idx) => (
                    <td key={idx} className={cn(s.cell, { [s.clickable]: onClickRow })}>
                      {col.renderCell(row, idx, rowIdx)}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
      {isLoading && data.length === 0 && <Loader isStatic={true} />}
    </>
  );
};

export type { IColumnConfig, IListProps };
export { List };
